import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FooterNew from '../components/FooterNew';
import { getBackendUrl, setAdminSession } from '../utils/auth';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    otp: '',
  });

  const backendUrl = useMemo(() => getBackendUrl(), []);

  const resetAlerts = () => {
    setError('');
    setMessage('');
  };

  const handleAdminLogin = async (event) => {
    event.preventDefault();
    resetAlerts();
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginData.email, password: loginData.password }),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Login failed');
      }

      setAdminSession(payload.token);
      navigate('/admin/social-posts', { replace: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-primary min-h-screen pt-28">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl p-7 sm:p-10"
          style={{
            background: 'linear-gradient(145deg, rgba(6, 11, 31, 0.95), rgba(10, 16, 41, 0.9))',
            border: '1px solid rgba(124, 77, 255, 0.2)',
            backdropFilter: 'blur(24px)',
          }}
        >
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium">
              Admin Portal
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-4">
              Admin Login
            </h1>
            <p className="text-slate-400 mt-2">
              Social media content panel access ke liye login credentials fill karein.
            </p>
          </div>

          {error ? <p className="text-rose-300 text-sm mb-4">{error}</p> : null}
          {message ? <p className="text-emerald-300 text-sm mb-4">{message}</p> : null}

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <input
              type="email"
              required
              value={loginData.email}
              onChange={(event) => setLoginData((prev) => ({ ...prev, email: event.target.value }))}
              placeholder="Admin Email"
              className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-[#7C4DFF]/25 text-white focus:outline-none focus:border-[#00D2FF] focus:ring-1 focus:ring-[#00D2FF]"
              style={{ background: 'rgba(6, 11, 31, 0.6)' }}
            />
            <input
              type="password"
              required
              value={loginData.password}
              onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Admin Password"
              className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-[#7C4DFF]/25 text-white focus:outline-none focus:border-[#00D2FF] focus:ring-1 focus:ring-[#00D2FF]"
              style={{ background: 'rgba(6, 11, 31, 0.6)' }}
            />

            <button type="submit" disabled={loading} className="neon-btn neon-btn-primary w-full justify-center group">
              <span>{loading ? 'Signing in...' : 'Login as Admin'}</span>
              <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </motion.div>
      </section>
      <FooterNew />
    </main>
  );
};

export default Login;
